import { action, computed, makeObservable, observable } from 'mobx';

import { ILocalStore } from 'stores/interfaces';
import { IntervalType } from 'utils/types';

type IntervalStoreOptions = {
  /** Функция, которая будет вызываться */
  callBack: (() => void) | (() => Promise<void>);
  /** Дожидаться ли окончания выполнения функции, перед запуском нового тика */
  awaitPrevRepeat?: boolean;
  /** Задержка между вызовами */
  timeout: number;
};

type PrivateFields = '_isActive' | '_tick';

/** Стор, который позволяет вызывать <callback> через опредённый промежуток <timeout> (ms) */
class CallbackIntervalStore implements ILocalStore {
  constructor(options: IntervalStoreOptions) {
    this._options = options;

    makeObservable<CallbackIntervalStore, PrivateFields>(this, {
      _isActive: observable.ref,
      isActive: computed,

      start: action.bound,
      stop: action.bound,
      restart: action.bound,

      reset: action.bound,

      _tick: action.bound,

      updateOptions: action.bound,
    });
  }

  private _options: IntervalStoreOptions;
  private _timeout: IntervalType | null = null;
  private _isActive = false;

  get isActive(): boolean {
    return this._isActive;
  }

  updateOptions(options: IntervalStoreOptions): void {
    this._options = options;
  }

  async start(): Promise<void> {
    if (this._isActive) {
      return;
    }

    this._isActive = true;
    await this._tick();
  }

  private async _tick(executeImmediately = false): Promise<void> {
    if (!this._isActive) {
      return;
    }

    const { callBack, awaitPrevRepeat, timeout } = this._options;

    if (executeImmediately) {
      if (awaitPrevRepeat) {
        await callBack();
      } else {
        callBack();
      }
    }

    this._timeout = setTimeout(() => {
      this._tick(true);
    }, timeout);
  }

  stop(): void {
    this.reset();
  }

  restart(): void {
    this.reset();
    this.start();
  }

  reset(): void {
    this._isActive = false;

    if (this._timeout) {
      clearTimeout(this._timeout);
    }
  }

  destroy(): void {
    this.reset();
  }
}

export default CallbackIntervalStore;
